import { useCallback, useState } from 'react';
import { Box, Flex, Text, UnstyledButton } from '@mantine/core';

import { useDeleteFormAttachment, useGetForm } from 'api/forms';
import { ConfirmationDialogModal } from 'components/shared';
import { EditIcon, FileIcon, TrashIcon } from 'icons';
import { useShowNotification } from 'hooks';
import type { FormAttachment } from 'types';

import { useTranslation } from 'react-i18next';
import { useFormEditContext } from '../../context';

import { AttachmentModal } from './AttachmentModal';

type Props = {
  attachment: FormAttachment;
};

export const AttachmentBox = ({ attachment }: Props) => {
  const { t } = useTranslation('forms');
  const { form } = useFormEditContext();
  const { refetchAttachment } = useFormEditContext();
  const { name, description, required } = attachment;
  const [editModalOpened, setEditModalOpened] = useState(false);

  const { mutateAsync: deleteFormAttachment } = useDeleteFormAttachment({
    formId: form.id,
  });
  const { refetch: refetchForm } = useGetForm({ id: form.id });

  const showNotification = useShowNotification();

  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const deleteAttachment = useCallback(async () => {
    try {
      await deleteFormAttachment({
        pathParams: { attachmentId: attachment.id },
      });
      await refetchAttachment();
      await refetchForm();
      showNotification({
        message: t('editView.sidebar.attachment.deleted'),
        variant: 'success',
      });
    } catch (error) {
      console.error(error);
      showNotification({
        message: t('editView.sidebar.attachment.deletedError'),
        variant: 'error',
      });
    }
  }, [showNotification]);

  const handleCloseEditModal = () => {
    setEditModalOpened(false);
    refetchAttachment();
  };

  return (
    <Flex align="center">
      <Flex
        sx={theme => ({
          padding: theme.other.spacing(1.25),
          background: theme.colors.gray[1],
          borderRadius: theme.radius.sm,
          marginRight: theme.other.spacing(2),
        })}
      >
        <FileIcon />
      </Flex>
      <Box sx={{ flex: 1 }}>
        <Text size="md">
          <b>{name}</b>
          {required && t('editView.sidebar.attachment.required')}
        </Text>
        {description && (
          <Text
            size="sm"
            sx={theme => ({ marginTop: theme.other.spacing(0.5) })}
          >
            {description}
          </Text>
        )}
      </Box>
      <Flex gap="sm">
        <UnstyledButton onClick={() => setEditModalOpened(true)}>
          <EditIcon color="blue" />
        </UnstyledButton>
        <AttachmentModal
          opened={editModalOpened}
          onClose={handleCloseEditModal}
          attachment={attachment}
        />
        <UnstyledButton onClick={() => setDeleteConfirmModalOpen(true)}>
          <TrashIcon color="red" />
        </UnstyledButton>
        <ConfirmationDialogModal
          title={t('editView.sidebar.attachment.deleteDialog.title')}
          message={t('editView.sidebar.attachment.deleteDialog.message')}
          confirmButtonText="Usuń"
          opened={deleteConfirmModalOpen}
          onClose={() => setDeleteConfirmModalOpen(false)}
          onConfirm={deleteAttachment}
        />
      </Flex>
    </Flex>
  );
};
